import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Check, Menu, X, ArrowRight, HelpCircle, Users, DollarSign, MapPin, Star, ChevronLeft, ChevronRight } from 'lucide-react';
import Logo from "./Logo";
import RentVsBuyCalculator from "./RentVsBuyCalculator";

// API Configuration
const API_URL = 'https://api.cloudmortgage.io';

// Hero slideshow images
const heroImages = [
  {
    url: "https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=90",
    alt: "Ngôi nhà đẹp hiện đại"
  },
  {
    url: "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=90",
    alt: "Biệt thự sang trọng"
  },
  {
    url: "https://images.unsplash.com/photo-1560518883-ce09059eeffa?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=90",
    alt: "Chìa khóa nhà mới"
  },
  {
    url: "https://images.unsplash.com/photo-1570129477492-45c003edd2be?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=90",
    alt: "Nhà ở gia đình"
  },
  {
    url: "https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=90",
    alt: "Nhà vườn đẹp"
  },
  {
    url: "https://images.unsplash.com/photo-1512917774080-9991f1c4c750?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=90",
    alt: "Biệt thự hiện đại Los Angeles"
  },
  {
    url: "https://images.unsplash.com/photo-1613490493576-7fde63acd811?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=90",
    alt: "Nhà sang trọng Beverly Hills"
  },
  {
    url: "https://images.unsplash.com/photo-1580587771525-78b9dba3b914?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=90",
    alt: "Biệt thự hồ bơi California"
  },
  {
    url: "https://images.unsplash.com/photo-1564013799919-ab600027ffc6?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=90",
    alt: "Nhà thiết kế hiện đại LA"
  },
  {
    url: "https://images.unsplash.com/photo-1555636222-cae831e670b3?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=90",
    alt: "Căn hộ cao cấp Manhattan NYC"
  },
  {
    url: "https://images.unsplash.com/photo-1480074568708-e7b720bb3f09?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=90",
    alt: "Brownstone Brooklyn New York"
  },
  {
    url: "https://images.unsplash.com/photo-1605276374104-dee2a0ed3cd6?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=90",
    alt: "Townhouse New York City"
  },
  {
    url: "https://images.unsplash.com/photo-1518780664697-55e3ad937233?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=90",
    alt: "Nhà Victorian San Francisco"
  },
  {
    url: "https://images.unsplash.com/photo-1449844908441-8829872d2607?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=90",
    alt: "Painted Ladies SF"
  },
  {
    url: "https://images.unsplash.com/photo-1502672260266-1c1ef2d93688?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=90",
    alt: "Penthouse view San Francisco"
  }
];

const LandingPage = () => {
  const [currentView, setCurrentView] = useState("landing");
  const [isVisible, setIsVisible] = useState(false);
  const [activeFeature, setActiveFeature] = useState(0);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [email, setEmail] = useState('');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showRateForm, setShowRateForm] = useState(false);
  const [formStep, setFormStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [formData, setFormData] = useState({
    // Step 1 - Thông tin liên hệ
    name: '',
    email: '',
    phone: '',
    // Step 2 - Chi tiết khoản vay
    occupancy: 'Nhà ở chính',
    propertyType: 'Nhà riêng',
    loanPurpose: 'Mua nhà',
    purchasePrice: '',
    downPayment: '',
    downPaymentPercent: '',
    zipCode: '',
    city: '',
    state: '',
    creditScore: '740 - 759',
    militaryVeteran: 'Không',
    notes: '',
    tcpaConsent: false,
    isOffMarket: false,
    // Mailing address for off-market leads (for direct mail postcards)
    mailingName: '',
    mailingAddressLine1: '',
    mailingAddressLine2: '',
    mailingCity: '',
    mailingState: '',
    mailingZip: ''
  });

  const handleFormChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const formatPhoneNumber = (value) => {
    const digits = value.replace(/\D/g, '');
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
  };

  const handlePhoneChange = (value) => {
    const formatted = formatPhoneNumber(value);
    setFormData(prev => ({ ...prev, phone: formatted }));
  };

  const isValidPhone = (phone) => {
    const digits = phone.replace(/\D/g, '');
    return digits.length >= 10;
  };

  const handleNextStep = () => {
    if (!formData.name || !formData.email || !formData.phone) {
      setSubmitError('Vui lòng điền đầy đủ thông tin');
      return;
    }
    if (!isValidPhone(formData.phone)) {
      setSubmitError('Vui lòng nhập số điện thoại hợp lệ (10 chữ số)');
      return;
    }
    setSubmitError('');
    setFormStep(2);
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();

    // Validate TCPA consent
    if (!formData.tcpaConsent) {
      setSubmitError('Vui lòng đồng ý với các điều khoản để tiếp tục.');
      return;
    }

    // Validate mailing address for off-market leads
    if (formData.isOffMarket) {
      if (!formData.mailingAddressLine1 || !formData.mailingAddressLine1.trim()) {
        setSubmitError('Vui lòng nhập địa chỉ đường cho off-market lead.');
        return;
      }
      if (!formData.mailingCity || !formData.mailingCity.trim()) {
        setSubmitError('Vui lòng nhập thành phố cho off-market lead.');
        return;
      }
      if (!formData.mailingState || formData.mailingState.length !== 2) {
        setSubmitError('Vui lòng nhập mã bang 2 ký tự (ví dụ: CA, TX).');
        return;
      }
      if (!formData.mailingZip || formData.mailingZip.length !== 5) {
        setSubmitError('Vui lòng nhập mã ZIP 5 chữ số.');
        return;
      }
    }

    setIsSubmitting(true);
    setSubmitError('');

    const phoneDigits = formData.phone.replace(/\D/g, '');
    const requestPayload = {
      source: 'nhamy',
      contact_name: formData.name,
      contact_email: formData.email,
      contact_phone: phoneDigits,
      loan_details: {
        occupancy: formData.occupancy,
        propertyType: formData.propertyType,
        loanPurpose: formData.loanPurpose,
        estimatedValue: formData.purchasePrice || undefined,
        downPayment: formData.downPayment || undefined,
        creditScore: formData.creditScore || undefined,
        city: formData.city || undefined,
        state: formData.state || undefined,
        zipCode: formData.zipCode || undefined,
        notes: formData.notes || undefined,
      },
      is_off_market: formData.isOffMarket,
      // Include mailing address for off-market leads (for direct mail postcards)
      mailing_address: formData.isOffMarket ? {
        name: formData.mailingName || formData.name,
        address_line1: formData.mailingAddressLine1,
        address_line2: formData.mailingAddressLine2 || undefined,
        address_city: formData.mailingCity,
        address_state: formData.mailingState,
        address_zip: formData.mailingZip
      } : undefined,
    };

    console.log('=== Nhamy Lead Submission ===');
    console.log('URL:', `${API_URL}/api/rapidrates/leads`);
    console.log('Payload:', JSON.stringify(requestPayload, null, 2));

    try {
      const response = await fetch(`${API_URL}/api/rapidrates/leads`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestPayload),
      });

      if (response.status === 429) {
        setSubmitError('Quá nhiều yêu cầu. Vui lòng đợi một chút và thử lại.');
        setIsSubmitting(false);
        return;
      }

      if (response.status === 401) {
        setSubmitError('Xác thực thất bại. Vui lòng thử lại sau.');
        setIsSubmitting(false);
        return;
      }

      if (!response.ok) {
        const error = await response.json();
        console.log('Response Status:', response.status);
        console.log('Response Error:', JSON.stringify(error, null, 2));

        let errorMsg = 'Gửi thất bại. Vui lòng thử lại.';
        const fieldNameMap = {
          'phone': 'số điện thoại',
          'email': 'email',
          'name': 'tên'
        };

        if (Array.isArray(error)) {
          const fieldErrors = error.map(err => {
            const field = err.loc?.[1] || 'field';
            const fieldName = fieldNameMap[field.replace('contact_', '')] || field.replace('contact_', '');
            if (err.type === 'string_too_short') {
              return `Vui lòng nhập ${fieldName} hợp lệ`;
            }
            return err.msg || `${fieldName} không hợp lệ`;
          });
          errorMsg = fieldErrors[0];
        } else if (error.detail && Array.isArray(error.detail)) {
          const fieldErrors = error.detail.map(err => {
            const field = err.loc?.[1] || 'field';
            const fieldName = fieldNameMap[field.replace('contact_', '')] || field.replace('contact_', '');
            if (err.type === 'string_too_short') {
              return `Vui lòng nhập ${fieldName} hợp lệ`;
            }
            return err.msg || `${fieldName} không hợp lệ`;
          });
          errorMsg = fieldErrors[0];
        } else if (typeof error.error === 'string') {
          errorMsg = error.error;
        } else if (typeof error.detail === 'string') {
          errorMsg = error.detail;
        }

        setSubmitError(errorMsg);
        setIsSubmitting(false);
        return;
      }

      // Success
      setSubmitSuccess(true);
      setIsSubmitting(false);

    } catch (error) {
      console.error('Submission error:', error);
      setSubmitError('Lỗi kết nối mạng. Vui lòng kiểm tra kết nối và thử lại.');
      setIsSubmitting(false);
    }
  };

  const closeForm = () => {
    setShowRateForm(false);
    setFormStep(1);
    setSubmitSuccess(false);
    setSubmitError('');
    setFormData({
      name: '',
      email: '',
      phone: '',
      occupancy: 'Nhà ở chính',
      propertyType: 'Nhà riêng',
      loanPurpose: 'Mua nhà',
      purchasePrice: '',
      downPayment: '',
      downPaymentPercent: '',
      zipCode: '',
      city: '',
      state: '',
      creditScore: '740 - 759',
      militaryVeteran: 'Không',
      notes: '',
      isOffMarket: false,
      mailingName: '',
      mailingAddressLine1: '',
      mailingAddressLine2: '',
      mailingCity: '',
      mailingState: '',
      mailingZip: ''
    });
  };

  // Animation trigger
  useEffect(() => {
    setIsVisible(true);
    const interval = setInterval(() => {
      setActiveFeature((prev) => (prev + 1) % 3);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  // Hero slideshow auto-rotation
  useEffect(() => {
    const slideInterval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroImages.length);
    }, 5000);
    return () => clearInterval(slideInterval);
  }, []);

  // Metricool Analytics
  useEffect(() => {
    const loadScript = (callback) => {
      const head = document.getElementsByTagName("head")[0];
      const script = document.createElement("script");
      script.type = "text/javascript";
      script.src = "https://tracker.metricool.com/resources/be.js";
      script.onreadystatechange = callback;
      script.onload = callback;
      head.appendChild(script);
    };
    loadScript(() => {
      if (window.beTracker) {
        window.beTracker.t({ hash: "12f29dbcab96b6e10522e947a57d8770" });
      }
    });
  }, []);

  // Placeholder for auth view
  if (currentView === 'auth') {
    return (
      <div className="min-h-screen bg-[#0a0f1c] flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-3xl font-bold text-white mb-4">Sắp Ra Mắt</h2>
          <p className="text-gray-400 mb-8">Chức năng đăng ký đang được thiết lập.</p>
          <button
            onClick={() => setCurrentView('landing')}
            className="px-6 py-3 bg-[#10b981] text-white rounded-full font-semibold hover:bg-[#059669] transition-all"
          >
            Quay Lại Trang Chủ
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0f1c] text-white overflow-hidden" style={{ fontFamily: "'DM Sans', sans-serif" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500;9..40,600;9..40,700&display=swap');

        .font-display { font-family: 'DM Sans', sans-serif; letter-spacing: -0.02em; }

        .gradient-text {
          color: #10b981;
          background: none;
          -webkit-text-fill-color: #10b981;
        }

        .glow-box {
          background: linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(16, 185, 129, 0.05) 100%);
          border: 1px solid rgba(16, 185, 129, 0.2);
          backdrop-filter: blur(20px);
        }

        .hero-glow {
          background: radial-gradient(ellipse 80% 50% at 50% -20%, rgba(16, 185, 129, 0.15) 0%, transparent 70%);
        }

        .floating {
          animation: float 6s ease-in-out infinite;
        }

        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-20px); }
        }

        .slide-up {
          animation: slideUp 0.8s ease-out forwards;
          opacity: 0;
          transform: translateY(40px);
        }

        @keyframes slideUp {
          to { opacity: 1; transform: translateY(0); }
        }

        .stagger-1 { animation-delay: 0.1s; }
        .stagger-2 { animation-delay: 0.2s; }
        .stagger-3 { animation-delay: 0.3s; }
        .stagger-4 { animation-delay: 0.4s; }
        .stagger-5 { animation-delay: 0.5s; }

        .grid-bg {
          background-image:
            linear-gradient(rgba(16, 185, 129, 0.03) 1px, transparent 1px),
            linear-gradient(90deg, rgba(16, 185, 129, 0.03) 1px, transparent 1px);
          background-size: 60px 60px;
        }

        .card-hover {
          transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
        }

        .card-hover:hover {
          transform: translateY(-8px);
          border-color: rgba(16, 185, 129, 0.4);
          box-shadow: 0 20px 40px rgba(16, 185, 129, 0.1);
        }

        .btn-primary {
          background: #10b981;
          transition: all 0.3s ease;
        }

        .btn-primary:hover {
          transform: scale(1.02);
          background: #059669;
          box-shadow: 0 10px 30px rgba(16, 185, 129, 0.3);
        }

        .feature-active {
          background: linear-gradient(135deg, rgba(16, 185, 129, 0.15) 0%, rgba(16, 185, 129, 0.08) 100%);
          border-color: rgba(16, 185, 129, 0.5);
        }
      `}</style>

      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 px-6 py-4 bg-[#0a0f1c]/90 backdrop-blur-md transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4'}`}>
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <Logo />
          <div className="hidden md:flex items-center gap-6">
            <Link
              to="/realtor"
              className="text-white/80 hover:text-white font-medium transition-colors"
            >
              Realtor Uy Tín
            </Link>
            <Link
              to="/blog"
              className="text-white/80 hover:text-white font-medium transition-colors"
            >
              Blog
            </Link>
            <button
              onClick={() => document.getElementById('faq-section').scrollIntoView({ behavior: 'smooth' })}
              className="text-white/80 hover:text-white font-medium transition-colors"
            >
              Câu Hỏi Thường Gặp
            </button>
            <Link
              to="/realtor/auth"
              className="px-4 py-2 bg-[#10b981]/20 border border-[#10b981]/40 text-[#10b981] rounded-full font-medium hover:bg-[#10b981] hover:text-white transition-all"
            >
              Realtor? Join us.
            </Link>
          </div>
          <button className="md:hidden text-white" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}>
            {isMobileMenuOpen ? <X /> : <Menu />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="absolute top-full left-0 right-0 bg-[#0a0f1c] border-b border-gray-800 p-6 md:hidden animate-in slide-in-from-top-5">
            <div className="flex flex-col gap-4">
              <Link
                to="/realtor"
                onClick={() => setIsMobileMenuOpen(false)}
                className="text-white/80 hover:text-white font-medium py-2"
              >
                Realtor Uy Tín
              </Link>
              <Link
                to="/blog"
                onClick={() => setIsMobileMenuOpen(false)}
                className="text-white/80 hover:text-white font-medium py-2"
              >
                Blog
              </Link>
              <button
                onClick={() => { document.getElementById('faq-section').scrollIntoView({ behavior: 'smooth' }); setIsMobileMenuOpen(false); }}
                className="text-white/80 hover:text-white font-medium py-2 text-left"
              >
                Câu Hỏi Thường Gặp
              </button>
              <Link
                to="/realtor/auth"
                onClick={() => setIsMobileMenuOpen(false)}
                className="text-[#10b981] hover:text-white font-medium py-2 border-t border-gray-800 pt-4"
              >
                Bạn là Realtor? Tham gia ngay
              </Link>
              <button onClick={() => { setShowRateForm(true); setIsMobileMenuOpen(false); }} className="btn-primary w-full py-3 rounded-lg text-white font-bold mt-2">Nhận Tư Vấn Ngay</button>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section - Full Screen Slideshow */}
      <section className="relative h-screen w-full overflow-hidden">
        {/* Full screen background slideshow */}
        <div className="absolute inset-0">
          {heroImages.map((image, index) => (
            <div
              key={index}
              className={`absolute inset-0 transition-opacity duration-1000 ${index === currentSlide ? 'opacity-100' : 'opacity-0'
                }`}
            >
              <img
                src={image.url}
                alt={image.alt}
                className="w-full h-full object-cover"
              />
            </div>
          ))}

          {/* Gradient overlays for readability */}
          <div className="absolute inset-0 bg-gradient-to-r from-[#0a0f1c]/90 via-[#0a0f1c]/70 to-transparent"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-[#0a0f1c]/80 via-transparent to-[#0a0f1c]/40"></div>
        </div>

        {/* Content overlay */}
        <div className="relative z-10 h-full flex items-center">
          <div className="max-w-7xl mx-auto px-6 w-full">
            <div className="max-w-2xl">
              <h1 className={`font-display font-extrabold text-4xl md:text-5xl lg:text-6xl xl:text-7xl leading-[0.95] tracking-tight mb-8 slide-up stagger-2 ${isVisible ? '' : 'opacity-0'}`}>
                <span className="block text-white drop-shadow-lg">Lãi Suất Tốt. Chuyên Gia Hàng Đầu.</span>
                <span className="gradient-text drop-shadow-lg">Giải Ngân Nhanh Chóng</span>
              </h1>

              <p className={`text-lg md:text-xl text-gray-200 max-w-xl mb-10 leading-relaxed slide-up stagger-3 ${isVisible ? '' : 'opacity-0'}`}>
                Giải pháp kết nối thông minh cho hành trình mua nhà tại Mỹ. Kết nối ngay với các chuyên gia đáng tin cậy giúp bạn chốt khoản vay phù hợp — Hiện thực hóa giấc mơ mua nhà Mỹ.
              </p>

              {/* CTA Buttons */}
              <div className={`flex flex-wrap gap-4 slide-up stagger-4 ${isVisible ? '' : 'opacity-0'}`}>
                <button onClick={() => setShowRateForm(true)} className="btn-primary px-8 py-4 rounded-full text-lg font-semibold text-white">
                  Nhận Tư Vấn Ngay
                </button>
              </div>

              {/* Stats Row */}
              <div className={`grid grid-cols-3 gap-6 mt-16 slide-up stagger-5 ${isVisible ? '' : 'opacity-0'}`}>
                {[
                  { value: '50K+', label: 'Kiểm Tra Lãi Suất Mỗi Ngày' },
                  { value: '$2.1B', label: 'Khối Lượng Vay Đã Phân Tích' },
                  { value: '99.9%', label: 'Tỷ Lệ Chính Xác' }
                ].map((stat, i) => (
                  <div key={i} className="text-left">
                    <div className="font-display font-bold text-2xl md:text-3xl lg:text-4xl text-white">{stat.value}</div>
                    <div className="text-xs md:text-sm text-gray-300 mt-1">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Slide navigation arrows */}
        <button
          onClick={() => setCurrentSlide((prev) => (prev - 1 + heroImages.length) % heroImages.length)}
          className="absolute left-6 top-1/2 -translate-y-1/2 z-20 w-12 h-12 bg-white/10 hover:bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center text-white transition-all border border-white/20"
        >
          <ChevronLeft className="w-6 h-6" />
        </button>
        <button
          onClick={() => setCurrentSlide((prev) => (prev + 1) % heroImages.length)}
          className="absolute right-6 top-1/2 -translate-y-1/2 z-20 w-12 h-12 bg-white/10 hover:bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center text-white transition-all border border-white/20"
        >
          <ChevronRight className="w-6 h-6" />
        </button>

        {/* Slide dots indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 flex gap-2">
          {heroImages.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentSlide(index)}
              className={`h-2 rounded-full transition-all ${index === currentSlide
                ? 'bg-[#10b981] w-8'
                : 'bg-white/50 hover:bg-white/80 w-2'
                }`}
            />
          ))}
        </div>

        {/* Floating card */}
        <div className="absolute bottom-8 right-8 z-20 hidden md:block">
          <div className="bg-white/10 backdrop-blur-md rounded-2xl p-5 border border-white/20">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-[#10b981] rounded-full flex items-center justify-center">
                <Check className="w-7 h-7 text-white" />
              </div>
              <div>
                <p className="text-white font-semibold text-lg">Phê Duyệt Nhanh</p>
                <p className="text-gray-300">Chỉ 5 ngày giải ngân</p>
              </div>
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-8 z-20 hidden md:flex flex-col items-center gap-2">
          <span className="text-white/60 text-sm">Cuộn xuống</span>
          <div className="w-6 h-10 rounded-full border-2 border-white/30 flex justify-center pt-2">
            <div className="w-1.5 h-3 bg-white/60 rounded-full animate-bounce"></div>
          </div>
        </div>
      </section>

      {/* Value Proposition Section */}
      <section className="relative py-24 px-6 bg-gradient-to-b from-[#0a0f1c] to-[#0d1424]">
        <div className="max-w-6xl mx-auto">
          {/* Main Headline */}
          <div className="text-center mb-16">
            <h2 className="font-display font-bold text-4xl md:text-5xl lg:text-6xl mb-4">
              Khoản Vay Của Bạn, <span className="gradient-text">Theo Cách Của Bạn</span>
            </h2>
            <div className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#10b981]/20 border border-[#10b981]/40 mt-4">
              <Check className="w-5 h-5 text-[#10b981]" />
              <span className="text-[#10b981] font-semibold text-lg">Đảm Bảo Hài Lòng 100%</span>
            </div>
          </div>

          {/* Rate Highlight */}
          <div className="text-center mb-16">
            <div className="inline-block glow-box rounded-3xl p-8 md:p-12">
              <p className="text-gray-400 text-lg mb-2">Lãi suất thấp chỉ từ</p>
              <div className="font-display font-extrabold text-6xl md:text-8xl gradient-text">5.5%</div>
              <p className="text-gray-400 text-xl mt-2">APR</p>
            </div>
          </div>

          {/* Benefits Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: '🚫', title: 'Không Cần Xác Minh Thu Nhập', desc: 'Không cần tài liệu chứng minh thu nhập' },
              { icon: '✓', title: 'Không Kiểm Tra Tín Dụng', desc: 'Được phê duyệt bất kể lịch sử tín dụng' },
              { icon: '💰', title: 'Không Phí Đóng Hồ Sơ', desc: 'Không phí đóng hồ sơ với các khoản vay đủ điều kiện' },
              { icon: '🔓', title: 'Không Phạt Trả Trước', desc: 'Trả hết nợ sớm mà không bị phạt' },
              { icon: '⚡', title: 'Đóng Hồ Sơ Nhanh 5 Ngày', desc: 'Hoàn tất khoản vay chỉ trong 5 ngày' },
              { icon: '🌍', title: 'Tất Cả Loại Vay', desc: 'Nhà ở, thương mại, vay nóng, người nước ngoài' }
            ].map((benefit, i) => (
              <div key={i} className="glow-box rounded-2xl p-6 card-hover">
                <div className="text-4xl mb-4">{benefit.icon}</div>
                <h3 className="font-display font-bold text-xl text-white mb-2">{benefit.title}</h3>
                <p className="text-gray-400">{benefit.desc}</p>
              </div>
            ))}
          </div>

          {/* Loan Types */}
          <div className="mt-16 text-center">
            <p className="text-gray-400 mb-6">Chúng tôi chuyên về tất cả các loại vay</p>
            <div className="flex flex-wrap justify-center gap-4">
              {['Nhà Ở', 'Thương Mại', 'Vay Nóng', 'Người Nước Ngoài'].map((type, i) => (
                <span key={i} className="px-6 py-3 rounded-full bg-[#141b2d] border border-gray-700 text-white font-medium hover:border-[#10b981]/50 transition-all cursor-default">
                  {type}
                </span>
              ))}
            </div>
          </div>

          {/* CTA */}
          <div className="mt-16 text-center">
            <button onClick={() => setShowRateForm(true)} className="btn-primary px-10 py-4 rounded-full text-lg font-semibold text-white">
              Nhận Tư Vấn Ngay
            </button>
            <p className="text-gray-500 text-sm mt-4">Không cam kết - Báo giá miễn phí trong vài phút</p>
          </div>
        </div>
      </section>

      {/* Rent vs Buy Calculator */}
      <RentVsBuyCalculator onGetQuote={() => setShowRateForm(true)} />

      {/* Low Interest Rate Section */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-display font-bold text-3xl md:text-4xl text-[#10b981] mb-6">
            Vay Mua Nhà Lãi Suất Thấp
          </h2>
          <p className="text-gray-600 text-lg leading-relaxed">
            Khi bạn đang tìm kiếm khoản vay mua nhà, bạn cần những nhà môi giới đáng tin cậy và minh bạch, những người sẽ giúp tìm kiếm và thương lượng với hơn 100 tổ chức cho vay để mang lại cho bạn lãi suất tốt nhất và thấp nhất có thể với chi phí thấp nhất hoặc không mất phí
          </p>
        </div>
      </section>

      {/* Accomplishments Section */}
      <section className="py-20 px-6 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-display font-bold text-3xl md:text-4xl text-[#10b981] mb-2">
              Thành Tựu Của Chúng Tôi
            </h2>
            <p className="text-gray-800 text-xl md:text-2xl font-semibold">
              Những Con Số Tự Nói Lên Tất Cả.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            {/* Customers */}
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                <Users className="w-12 h-12 text-[#10b981]" />
              </div>
              <div className="font-display font-bold text-4xl md:text-5xl text-gray-800 mb-2">2,000 +</div>
              <p className="text-gray-600">Khách Hàng</p>
            </div>

            {/* Closed Loans */}
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                <DollarSign className="w-12 h-12 text-[#10b981]" />
              </div>
              <div className="font-display font-bold text-4xl md:text-5xl text-gray-800 mb-2">1,100 Tỷ</div>
              <p className="text-gray-600">Tổng Giá Trị Vay Đã Giải Ngân</p>
            </div>

            {/* Licensed States */}
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                <MapPin className="w-12 h-12 text-[#10b981]" />
              </div>
              <div className="font-display font-bold text-4xl md:text-5xl text-gray-800 mb-2">40</div>
              <p className="text-gray-600">Bang Có Giấy Phép</p>
            </div>
          </div>
        </div>
      </section>

      {/* Customer Reviews Section */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <h2 className="font-display font-bold text-3xl md:text-4xl text-[#10b981] text-center mb-16">
            Khách Hàng Nói Gì Về Chúng Tôi
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            {/* Google Reviews */}
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4">
                <svg viewBox="0 0 24 24" className="w-full h-full">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                </svg>
              </div>
              <div className="flex justify-center gap-1 mb-2">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-6 h-6 fill-[#10b981] text-[#10b981]" />
                ))}
              </div>
              <p className="text-gray-600">5.0 Sao, 80+ đánh giá</p>
            </div>

            {/* Zillow Reviews */}
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 bg-[#006AFF] rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-xl">Zillow</span>
              </div>
              <div className="flex justify-center gap-1 mb-2">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-6 h-6 fill-[#10b981] text-[#10b981]" />
                ))}
              </div>
              <p className="text-gray-600">5.0 Sao, 70+ đánh giá</p>
            </div>

            {/* Facebook Reviews */}
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 bg-[#1877F2] rounded-full flex items-center justify-center">
                <span className="text-white font-bold text-3xl">f</span>
              </div>
              <div className="flex justify-center gap-1 mb-2">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-6 h-6 fill-[#10b981] text-[#10b981]" />
                ))}
              </div>
              <p className="text-gray-600">5.0 Sao, 16K+ lượt thích</p>
            </div>
          </div>
        </div>
      </section>

      {/* Lending Networks Section */}
      <section className="py-20 px-6 bg-[#f0fdf4]">
        <div className="max-w-6xl mx-auto">
          <h2 className="font-display font-bold text-2xl md:text-3xl text-[#10b981] text-center mb-12">
            Tìm Lãi Suất Tốt Nhất Từ Hơn 100 Mạng Lưới Cho Vay
          </h2>

          <div className="flex items-center justify-center gap-4 md:gap-8">
            <button className="p-2 text-gray-400 hover:text-gray-600 transition-colors">
              <ChevronLeft className="w-6 h-6" />
            </button>

            <div className="flex items-center gap-8 md:gap-16 overflow-hidden">
              {/* Homepoint */}
              <div className="flex-shrink-0">
                <span className="text-gray-400 font-semibold text-lg md:text-xl tracking-wide">homepoint</span>
              </div>

              {/* Newrez */}
              <div className="flex-shrink-0 flex items-center gap-1">
                <span className="text-gray-400 font-bold text-lg md:text-xl">∧</span>
                <span className="text-gray-400 font-semibold text-lg md:text-xl">newrez</span>
              </div>

              {/* PNC */}
              <div className="flex-shrink-0">
                <span className="text-gray-400 font-bold text-xl md:text-2xl">◎ PNC</span>
              </div>

              {/* Quicken Loans */}
              <div className="flex-shrink-0 text-center">
                <span className="text-gray-400 font-bold text-lg md:text-xl leading-tight block">Quicken</span>
                <span className="text-gray-400 font-bold text-lg md:text-xl leading-tight">Loans</span>
              </div>

              {/* UWM */}
              <div className="flex-shrink-0 text-center">
                <span className="text-gray-400 font-bold text-xl md:text-2xl">UWM</span>
                <div className="flex justify-center gap-0.5 mt-0.5">
                  <div className="w-8 h-0.5 bg-gray-400"></div>
                  <div className="w-8 h-0.5 bg-gray-400"></div>
                </div>
              </div>

              {/* Wells Fargo */}
              <div className="flex-shrink-0 text-center">
                <span className="text-gray-400 font-bold text-lg md:text-xl leading-tight block">WELLS</span>
                <span className="text-gray-400 font-bold text-lg md:text-xl leading-tight">FARGO</span>
              </div>
            </div>

            <button className="p-2 text-gray-400 hover:text-gray-600 transition-colors">
              <ChevronRight className="w-6 h-6" />
            </button>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq-section" className="py-20 px-6 bg-[#0a0f1c]">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-display font-bold text-3xl md:text-4xl text-white mb-4">
              Câu Hỏi <span className="text-[#10b981]">Thường Gặp</span>
            </h2>
            <p className="text-gray-400 text-lg">
              Giải đáp những thắc mắc phổ biến về vay mua nhà
            </p>
          </div>

          <div className="space-y-4">
            {/* FAQ Item 1 */}
            <details className="group bg-[#141b2d] rounded-2xl border border-gray-800 overflow-hidden">
              <summary className="flex items-center justify-between p-6 cursor-pointer list-none">
                <span className="text-white font-semibold text-lg">Quy trình vay mua nhà mất bao lâu?</span>
                <ChevronRight className="w-5 h-5 text-[#10b981] transition-transform group-open:rotate-90" />
              </summary>
              <div className="px-6 pb-6 text-gray-400">
                Thông thường từ 21-30 ngày kể từ khi hồ sơ đầy đủ. Với chương trình đặc biệt của chúng tôi, một số trường hợp có thể đóng hồ sơ trong 5 ngày làm việc.
              </div>
            </details>

            {/* FAQ Item 2 */}
            <details className="group bg-[#141b2d] rounded-2xl border border-gray-800 overflow-hidden">
              <summary className="flex items-center justify-between p-6 cursor-pointer list-none">
                <span className="text-white font-semibold text-lg">Tôi cần chuẩn bị những giấy tờ gì?</span>
                <ChevronRight className="w-5 h-5 text-[#10b981] transition-transform group-open:rotate-90" />
              </summary>
              <div className="px-6 pb-6 text-gray-400">
                <p className="mb-3">Tùy vào loại khoản vay, bạn có thể cần:</p>
                <ul className="list-disc list-inside space-y-1">
                  <li>2 năm W-2 hoặc 1099 gần nhất</li>
                  <li>2 tháng sao kê ngân hàng (bank statements)</li>
                  <li>2 năm khai thuế (tax returns)</li>
                  <li>ID và SSN/ITIN</li>
                </ul>
                <p className="mt-3 text-[#10b981]">Với chương trình Bank Statement Loan, bạn chỉ cần sao kê ngân hàng mà không cần xác minh thu nhập truyền thống.</p>
              </div>
            </details>

            {/* FAQ Item 3 */}
            <details className="group bg-[#141b2d] rounded-2xl border border-gray-800 overflow-hidden">
              <summary className="flex items-center justify-between p-6 cursor-pointer list-none">
                <span className="text-white font-semibold text-lg">Điểm tín dụng tối thiểu cần bao nhiêu?</span>
                <ChevronRight className="w-5 h-5 text-[#10b981] transition-transform group-open:rotate-90" />
              </summary>
              <div className="px-6 pb-6 text-gray-400">
                <ul className="space-y-2">
                  <li><span className="text-white">Conventional Loan:</span> từ 620+</li>
                  <li><span className="text-white">FHA Loan:</span> từ 580+</li>
                  <li><span className="text-white">Bank Statement Loan:</span> từ 620+</li>
                  <li><span className="text-white">DSCR/Investor Loan:</span> từ 640+</li>
                </ul>
              </div>
            </details>

            {/* FAQ Item 4 */}
            <details className="group bg-[#141b2d] rounded-2xl border border-gray-800 overflow-hidden">
              <summary className="flex items-center justify-between p-6 cursor-pointer list-none">
                <span className="text-white font-semibold text-lg">Tôi mới sang Mỹ, chưa có lịch sử tín dụng. Tôi có vay được không?</span>
                <ChevronRight className="w-5 h-5 text-[#10b981] transition-transform group-open:rotate-90" />
              </summary>
              <div className="px-6 pb-6 text-gray-400">
                Có! Chúng tôi có chương trình dành riêng cho người mới đến Mỹ (Foreign National Loan). Bạn có thể sử dụng hộ chiếu, visa, và chứng minh thu nhập từ nước ngoài để đủ điều kiện.
              </div>
            </details>

            {/* FAQ Item 5 */}
            <details className="group bg-[#141b2d] rounded-2xl border border-gray-800 overflow-hidden">
              <summary className="flex items-center justify-between p-6 cursor-pointer list-none">
                <span className="text-white font-semibold text-lg">Tôi tự kinh doanh (self-employed). Có khó vay hơn không?</span>
                <ChevronRight className="w-5 h-5 text-[#10b981] transition-transform group-open:rotate-90" />
              </summary>
              <div className="px-6 pb-6 text-gray-400">
                Không nhất thiết. Chúng tôi có chương trình Bank Statement Loan dành riêng cho người tự kinh doanh. Chỉ cần 12-24 tháng sao kê ngân hàng, không cần nộp thuế.
              </div>
            </details>

            {/* FAQ Item 6 */}
            <details className="group bg-[#141b2d] rounded-2xl border border-gray-800 overflow-hidden">
              <summary className="flex items-center justify-between p-6 cursor-pointer list-none">
                <span className="text-white font-semibold text-lg">Tôi cần trả trước bao nhiêu phần trăm?</span>
                <ChevronRight className="w-5 h-5 text-[#10b981] transition-transform group-open:rotate-90" />
              </summary>
              <div className="px-6 pb-6 text-gray-400">
                <ul className="space-y-2">
                  <li><span className="text-white">Conventional:</span> từ 3% - 20%</li>
                  <li><span className="text-white">FHA:</span> từ 3.5%</li>
                  <li><span className="text-white">VA Loan (cho cựu chiến binh):</span> 0%</li>
                  <li><span className="text-white">Jumbo Loan:</span> từ 10% - 20%</li>
                  <li><span className="text-white">Investment Property:</span> từ 15% - 25%</li>
                </ul>
              </div>
            </details>

            {/* FAQ Item 7 */}
            <details className="group bg-[#141b2d] rounded-2xl border border-gray-800 overflow-hidden">
              <summary className="flex items-center justify-between p-6 cursor-pointer list-none">
                <span className="text-white font-semibold text-lg">Có phí trả trước khoản vay sớm không?</span>
                <ChevronRight className="w-5 h-5 text-[#10b981] transition-transform group-open:rotate-90" />
              </summary>
              <div className="px-6 pb-6 text-gray-400">
                Không. Tất cả khoản vay của chúng tôi đều không có phạt trả trước (no prepayment penalty). Bạn có thể trả hết nợ bất cứ lúc nào mà không mất thêm chi phí.
              </div>
            </details>

            {/* FAQ Item 8 */}
            <details className="group bg-[#141b2d] rounded-2xl border border-gray-800 overflow-hidden">
              <summary className="flex items-center justify-between p-6 cursor-pointer list-none">
                <span className="text-white font-semibold text-lg">Tại sao nên chọn Nhamy.us?</span>
                <ChevronRight className="w-5 h-5 text-[#10b981] transition-transform group-open:rotate-90" />
              </summary>
              <div className="px-6 pb-6 text-gray-400">
                <ul className="space-y-2">
                  <li><span className="text-[#10b981]">✓</span> <span className="text-white">Nói tiếng Việt:</span> Giao tiếp dễ dàng, hiểu rõ nhu cầu của bạn</li>
                  <li><span className="text-[#10b981]">✓</span> <span className="text-white">So sánh 100+ lender:</span> Tìm lãi suất thấp nhất</li>
                  <li><span className="text-[#10b981]">✓</span> <span className="text-white">Đóng hồ sơ nhanh:</span> Có thể từ 5 ngày</li>
                  <li><span className="text-[#10b981]">✓</span> <span className="text-white">Miễn phí tư vấn:</span> Không mất phí cho đến khi đóng hồ sơ</li>
                  <li><span className="text-[#10b981]">✓</span> <span className="text-white">Kinh nghiệm:</span> 2,000+ khách hàng hài lòng</li>
                </ul>
              </div>
            </details>
          </div>

          {/* CTA */}
          <div className="mt-12 text-center">
            <p className="text-gray-400 mb-4">Còn thắc mắc? Liên hệ ngay với chúng tôi để được tư vấn miễn phí!</p>
            <button onClick={() => setShowRateForm(true)} className="btn-primary px-8 py-4 rounded-full text-lg font-semibold text-white">
              Nhận Tư Vấn Ngay
            </button>
          </div>
        </div>
      </section>

      {/* Rate Search Form Modal */}
      {showRateForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
          <div className="bg-white rounded-lg shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="bg-[#10b981] px-6 py-4 flex items-center justify-between sticky top-0 z-10">
              <div>
                <h2 className="text-white font-semibold text-lg">
                  {submitSuccess ? 'Cảm Ơn Bạn!' : formStep === 1 ? 'Nhận Báo Giá' : 'Chi Tiết Khoản Vay'}
                </h2>
                {!submitSuccess && <p className="text-white/70 text-sm">Bước {formStep} / 2</p>}
              </div>
              <button
                onClick={closeForm}
                className="text-white/80 hover:text-white transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Success State */}
            {submitSuccess ? (
              <div className="p-8 text-center">
                <div className="w-16 h-16 bg-[#10b981]/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Check className="w-8 h-8 text-[#10b981]" />
                </div>
                <h3 className="text-2xl font-bold text-gray-800 mb-2">Đã Nhận Yêu Cầu!</h3>
                <p className="text-gray-600 mb-6">
                  Cảm ơn bạn, {formData.name}! Chúng tôi đã nhận được yêu cầu báo giá của bạn và sẽ liên hệ với bạn tại {formData.email} trong thời gian sớm nhất với các lựa chọn lãi suất phù hợp.
                </p>
                <button
                  onClick={closeForm}
                  className="px-8 py-3 bg-[#10b981] hover:bg-[#059669] text-white font-semibold rounded-md transition-colors"
                >
                  Đóng
                </button>
              </div>
            ) : (
              <>
                {/* Step Indicator */}
                <div className="px-6 pt-4">
                  <div className="flex items-center gap-2">
                    <div className={`flex-1 h-2 rounded-full ${formStep >= 1 ? 'bg-[#10b981]' : 'bg-gray-200'}`}></div>
                    <div className={`flex-1 h-2 rounded-full ${formStep >= 2 ? 'bg-[#10b981]' : 'bg-gray-200'}`}></div>
                  </div>
                </div>

                {/* Step 1: Contact Info */}
                {formStep === 1 && (
                  <div className="p-6 space-y-5">
                    <p className="text-gray-600 text-sm">Vui lòng cung cấp thông tin liên hệ để bắt đầu.</p>

                    {/* Name */}
                    <div>
                      <label className="block text-gray-800 font-semibold mb-2">
                        Họ Và Tên <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={formData.name}
                        onChange={(e) => handleFormChange('name', e.target.value)}
                        placeholder="Nguyễn Văn A"
                        className="w-full px-4 py-3 border border-gray-300 rounded-md text-gray-700 focus:outline-none focus:border-[#10b981] focus:ring-1 focus:ring-[#10b981]"
                      />
                    </div>

                    {/* Email */}
                    <div>
                      <label className="block text-gray-800 font-semibold mb-2">
                        Địa Chỉ Email <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="email"
                        value={formData.email}
                        onChange={(e) => handleFormChange('email', e.target.value)}
                        placeholder="email@example.com"
                        className="w-full px-4 py-3 border border-gray-300 rounded-md text-gray-700 focus:outline-none focus:border-[#10b981] focus:ring-1 focus:ring-[#10b981]"
                      />
                    </div>

                    {/* Phone */}
                    <div>
                      <label className="block text-gray-800 font-semibold mb-2">
                        Số Điện Thoại <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => handlePhoneChange(e.target.value)}
                        placeholder="(555) 123-4567"
                        className="w-full px-4 py-3 border border-gray-300 rounded-md text-gray-700 focus:outline-none focus:border-[#10b981] focus:ring-1 focus:ring-[#10b981]"
                      />
                    </div>

                    {/* Error Message */}
                    {submitError && (
                      <div className="p-3 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm">
                        {submitError}
                      </div>
                    )}

                    {/* Next Button */}
                    <div className="flex justify-end pt-4">
                      <button
                        type="button"
                        onClick={() => { setSubmitError(''); handleNextStep(); }}
                        className="px-8 py-3 bg-[#10b981] hover:bg-[#059669] text-white font-semibold rounded-md transition-colors flex items-center gap-2"
                      >
                        Tiếp Theo <ArrowRight className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                )}

                {/* Step 2: Loan Details */}
                {formStep === 2 && (
                  <form onSubmit={handleFormSubmit} className="p-6 space-y-5">
                    {/* Occupancy */}
                    <div>
                      <label className="flex items-center gap-2 text-gray-800 font-semibold mb-2">
                        Mục Đích Sử Dụng
                        <HelpCircle className="w-5 h-5 text-[#10b981]" />
                      </label>
                      <select
                        value={formData.occupancy}
                        onChange={(e) => handleFormChange('occupancy', e.target.value)}
                        className="w-full px-4 py-3 border border-gray-300 rounded-md text-gray-700 focus:outline-none focus:border-[#10b981] focus:ring-1 focus:ring-[#10b981]"
                      >
                        <option>Nhà Ở Chính</option>
                        <option>Nhà Ở Phụ</option>
                        <option>Bất Động Sản Đầu Tư</option>
                      </select>
                    </div>

                    {/* Property Type */}
                    <div>
                      <label className="flex items-center gap-2 text-gray-800 font-semibold mb-2">
                        Loại Bất Động Sản
                        <HelpCircle className="w-5 h-5 text-[#10b981]" />
                      </label>
                      <select
                        value={formData.propertyType}
                        onChange={(e) => handleFormChange('propertyType', e.target.value)}
                        className="w-full px-4 py-3 border border-gray-300 rounded-md text-gray-700 focus:outline-none focus:border-[#10b981] focus:ring-1 focus:ring-[#10b981]"
                      >
                        <option>Nhà Riêng</option>
                        <option>Căn Hộ</option>
                        <option>Nhà Phố</option>
                        <option>Nhà Nhiều Căn Hộ (2-4 căn)</option>
                        <option>Thương Mại</option>
                      </select>
                    </div>

                    {/* Loan Purpose */}
                    <div>
                      <label className="flex items-center gap-2 text-gray-800 font-semibold mb-2">
                        Mục Đích Vay
                        <HelpCircle className="w-5 h-5 text-[#10b981]" />
                      </label>
                      <select
                        value={formData.loanPurpose}
                        onChange={(e) => handleFormChange('loanPurpose', e.target.value)}
                        className="w-full px-4 py-3 border border-gray-300 rounded-md text-gray-700 focus:outline-none focus:border-[#10b981] focus:ring-1 focus:ring-[#10b981]"
                      >
                        <option>Mua Nhà</option>
                        <option>Tái Cấp Vốn</option>
                        <option>Rút Tiền Mặt</option>
                      </select>
                    </div>

                    {/* Purchase Price */}
                    <div>
                      <label className="flex items-center gap-2 text-gray-800 font-semibold mb-2">
                        Giá Mua
                        <HelpCircle className="w-5 h-5 text-[#10b981]" />
                      </label>
                      <div className="relative">
                        <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                        <input
                          type="text"
                          value={formData.purchasePrice}
                          onChange={(e) => handleFormChange('purchasePrice', e.target.value)}
                          placeholder="500,000"
                          className="w-full pl-8 pr-4 py-3 border border-gray-300 rounded-md text-gray-700 focus:outline-none focus:border-[#10b981] focus:ring-1 focus:ring-[#10b981]"
                        />
                      </div>
                    </div>

                    {/* Down Payment */}
                    <div>
                      <label className="flex items-center gap-2 text-gray-800 font-semibold mb-2">
                        Tiền Trả Trước
                        <HelpCircle className="w-5 h-5 text-[#10b981]" />
                      </label>
                      <div className="flex gap-2">
                        <div className="relative flex-1">
                          <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                          <input
                            type="text"
                            value={formData.downPayment}
                            onChange={(e) => handleFormChange('downPayment', e.target.value)}
                            placeholder="100,000"
                            className="w-full pl-8 pr-4 py-3 border border-gray-300 rounded-md text-gray-700 focus:outline-none focus:border-[#10b981] focus:ring-1 focus:ring-[#10b981]"
                          />
                        </div>
                        <div className="relative w-24">
                          <input
                            type="text"
                            value={formData.downPaymentPercent}
                            onChange={(e) => handleFormChange('downPaymentPercent', e.target.value)}
                            placeholder="20"
                            className="w-full px-4 py-3 border border-gray-300 rounded-md text-gray-700 focus:outline-none focus:border-[#10b981] focus:ring-1 focus:ring-[#10b981] text-right pr-8"
                          />
                          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">%</span>
                        </div>
                      </div>
                    </div>

                    {/* City */}
                    <div>
                      <label className="flex items-center gap-2 text-gray-800 font-semibold mb-2">
                        Thành Phố
                        <HelpCircle className="w-5 h-5 text-[#10b981]" />
                      </label>
                      <input
                        type="text"
                        value={formData.city}
                        onChange={(e) => handleFormChange('city', e.target.value)}
                        placeholder="Los Angeles"
                        className="w-full px-4 py-3 border border-gray-300 rounded-md text-gray-700 focus:outline-none focus:border-[#10b981] focus:ring-1 focus:ring-[#10b981]"
                      />
                    </div>

                    {/* State */}
                    <div>
                      <label className="flex items-center gap-2 text-gray-800 font-semibold mb-2">
                        Tiểu Bang
                        <HelpCircle className="w-5 h-5 text-[#10b981]" />
                      </label>
                      <input
                        type="text"
                        value={formData.state}
                        onChange={(e) => handleFormChange('state', e.target.value)}
                        placeholder="California"
                        className="w-full px-4 py-3 border border-gray-300 rounded-md text-gray-700 focus:outline-none focus:border-[#10b981] focus:ring-1 focus:ring-[#10b981]"
                      />
                    </div>

                    {/* Zip Code */}
                    <div>
                      <label className="flex items-center gap-2 text-gray-800 font-semibold mb-2">
                        Mã Vùng
                        <HelpCircle className="w-5 h-5 text-[#10b981]" />
                      </label>
                      <input
                        type="text"
                        value={formData.zipCode}
                        onChange={(e) => handleFormChange('zipCode', e.target.value)}
                        placeholder="90210"
                        className="w-full px-4 py-3 border border-gray-300 rounded-md text-gray-700 focus:outline-none focus:border-[#10b981] focus:ring-1 focus:ring-[#10b981]"
                      />
                    </div>

                    {/* Credit Score */}
                    <div>
                      <label className="flex items-center gap-2 text-gray-800 font-semibold mb-2">
                        Điểm Tín Dụng
                        <HelpCircle className="w-5 h-5 text-[#10b981]" />
                      </label>
                      <select
                        value={formData.creditScore}
                        onChange={(e) => handleFormChange('creditScore', e.target.value)}
                        className="w-full px-4 py-3 border border-gray-300 rounded-md text-gray-700 focus:outline-none focus:border-[#10b981] focus:ring-1 focus:ring-[#10b981]"
                      >
                        <option>780+</option>
                        <option>760 - 779</option>
                        <option>740 - 759</option>
                        <option>720 - 739</option>
                        <option>700 - 719</option>
                        <option>680 - 699</option>
                        <option>660 - 679</option>
                        <option>640 - 659</option>
                        <option>620 - 639</option>
                        <option>Dưới 620</option>
                      </select>
                    </div>

                    {/* Military or Veteran */}
                    <div>
                      <label className="flex items-center gap-2 text-gray-800 font-semibold mb-2">
                        Quân Nhân hoặc Cựu Chiến Binh
                        <HelpCircle className="w-5 h-5 text-[#10b981]" />
                      </label>
                      <select
                        value={formData.militaryVeteran}
                        onChange={(e) => handleFormChange('militaryVeteran', e.target.value)}
                        className="w-full px-4 py-3 border border-gray-300 rounded-md text-gray-700 focus:outline-none focus:border-[#10b981] focus:ring-1 focus:ring-[#10b981]"
                      >
                        <option>Không</option>
                        <option>Có</option>
                      </select>
                    </div>

                    {/* Notes */}
                    <div>
                      <label className="flex items-center gap-2 text-gray-800 font-semibold mb-2">
                        Ghi Chú
                        <HelpCircle className="w-5 h-5 text-[#10b981]" />
                      </label>
                      <textarea
                        value={formData.notes}
                        onChange={(e) => handleFormChange('notes', e.target.value)}
                        placeholder="Thông tin bổ sung cho đối tác cho vay (ví dụ: tình trạng việc làm, thu nhập đặc biệt, yêu cầu cụ thể...)"
                        rows={4}
                        className="w-full px-4 py-3 border border-gray-300 rounded-md text-gray-700 focus:outline-none focus:border-[#10b981] focus:ring-1 focus:ring-[#10b981] resize-none"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Tùy chọn - Chia sẻ thêm thông tin để giúp chúng tôi tìm khoản vay phù hợp nhất cho bạn.
                      </p>
                    </div>

                    {/* Off-Market Lead Checkbox */}
                    <div className="pt-2">
                      <label className="flex items-start gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.isOffMarket}
                          onChange={(e) => handleFormChange('isOffMarket', e.target.checked)}
                          className="mt-1 w-5 h-5 text-[#10b981] border-gray-300 rounded focus:ring-[#10b981] cursor-pointer"
                        />
                        <span className="text-sm text-gray-700">
                          Off-Market Lead
                        </span>
                      </label>
                    </div>

                    {/* Mailing Address Form - Only shown when Off-Market is checked */}
                    {formData.isOffMarket && (
                      <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200 space-y-4">
                        <div className="flex items-center gap-2 mb-2">
                          <svg className="w-5 h-5 text-[#10b981]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                          </svg>
                          <span className="text-gray-800 font-semibold">Địa Chỉ Nhận Thư (Direct Mail)</span>
                        </div>
                        <p className="text-xs text-gray-500 mb-3">
                          Chúng tôi sẽ gửi postcard marketing đến địa chỉ này cho khách hàng off-market.
                        </p>

                        {/* Recipient Name */}
                        <div>
                          <label className="block text-gray-700 font-medium mb-1 text-sm">
                            Tên Người Nhận
                          </label>
                          <input
                            type="text"
                            value={formData.mailingName}
                            onChange={(e) => handleFormChange('mailingName', e.target.value)}
                            placeholder="Nguyễn Văn A"
                            className="w-full px-3 py-2 border border-gray-300 rounded-md text-gray-700 text-sm focus:outline-none focus:border-[#10b981] focus:ring-1 focus:ring-[#10b981]"
                          />
                          <p className="text-xs text-gray-400 mt-1">Để trống để dùng tên liên hệ ở trên</p>
                        </div>

                        {/* Address Line 1 */}
                        <div>
                          <label className="block text-gray-700 font-medium mb-1 text-sm">
                            Địa Chỉ Đường <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            value={formData.mailingAddressLine1}
                            onChange={(e) => handleFormChange('mailingAddressLine1', e.target.value)}
                            placeholder="123 Main Street"
                            className="w-full px-3 py-2 border border-gray-300 rounded-md text-gray-700 text-sm focus:outline-none focus:border-[#10b981] focus:ring-1 focus:ring-[#10b981]"
                          />
                        </div>

                        {/* Address Line 2 */}
                        <div>
                          <label className="block text-gray-700 font-medium mb-1 text-sm">
                            Apt / Suite / Unit
                          </label>
                          <input
                            type="text"
                            value={formData.mailingAddressLine2}
                            onChange={(e) => handleFormChange('mailingAddressLine2', e.target.value)}
                            placeholder="Apt 4B (tùy chọn)"
                            className="w-full px-3 py-2 border border-gray-300 rounded-md text-gray-700 text-sm focus:outline-none focus:border-[#10b981] focus:ring-1 focus:ring-[#10b981]"
                          />
                        </div>

                        {/* City, State, Zip Row */}
                        <div className="grid grid-cols-6 gap-2">
                          {/* City */}
                          <div className="col-span-3">
                            <label className="block text-gray-700 font-medium mb-1 text-sm">
                              Thành Phố <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="text"
                              value={formData.mailingCity}
                              onChange={(e) => handleFormChange('mailingCity', e.target.value)}
                              placeholder="Los Angeles"
                              className="w-full px-3 py-2 border border-gray-300 rounded-md text-gray-700 text-sm focus:outline-none focus:border-[#10b981] focus:ring-1 focus:ring-[#10b981]"
                            />
                          </div>

                          {/* State */}
                          <div className="col-span-1">
                            <label className="block text-gray-700 font-medium mb-1 text-sm">
                              Bang <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="text"
                              value={formData.mailingState}
                              onChange={(e) => handleFormChange('mailingState', e.target.value.toUpperCase().slice(0, 2))}
                              placeholder="CA"
                              maxLength={2}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md text-gray-700 text-sm focus:outline-none focus:border-[#10b981] focus:ring-1 focus:ring-[#10b981] uppercase"
                            />
                          </div>

                          {/* Zip */}
                          <div className="col-span-2">
                            <label className="block text-gray-700 font-medium mb-1 text-sm">
                              ZIP <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="text"
                              value={formData.mailingZip}
                              onChange={(e) => handleFormChange('mailingZip', e.target.value.replace(/\D/g, '').slice(0, 5))}
                              placeholder="90210"
                              maxLength={5}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md text-gray-700 text-sm focus:outline-none focus:border-[#10b981] focus:ring-1 focus:ring-[#10b981]"
                            />
                          </div>
                        </div>
                      </div>
                    )}

                    {/* TCPA Consent */}
                    <div className="pt-2">
                      <label className="flex items-start gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.tcpaConsent}
                          onChange={(e) => handleFormChange('tcpaConsent', e.target.checked)}
                          className="mt-1 w-5 h-5 text-[#10b981] border-gray-300 rounded focus:ring-[#10b981] cursor-pointer"
                        />
                        <span className="text-xs text-gray-600 leading-relaxed">
                          Tôi đồng ý với{' '}
                          <Link to="/terms" target="_blank" className="text-[#10b981] underline hover:text-[#059669]">
                            Điều Khoản Dịch Vụ
                          </Link>{' '}
                          và{' '}
                          <Link to="/privacy" target="_blank" className="text-[#10b981] underline hover:text-[#059669]">
                            Chính Sách Bảo Mật
                          </Link>, bao gồm việc đồng ý để Nhamy.us và các đối tác cho vay liên hệ với tôi.
                        </span>
                      </label>
                    </div>

                    {/* Error Message */}
                    {submitError && (
                      <div className="p-3 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm">
                        {submitError}
                      </div>
                    )}

                    {/* Buttons */}
                    <div className="flex justify-between pt-4">
                      <button
                        type="button"
                        onClick={() => setFormStep(1)}
                        disabled={isSubmitting}
                        className="px-6 py-3 border border-gray-300 text-gray-700 font-semibold rounded-md hover:bg-gray-50 transition-colors disabled:opacity-50"
                      >
                        Quay Lại
                      </button>
                      <button
                        type="submit"
                        disabled={isSubmitting}
                        className="px-8 py-3 bg-[#10b981] hover:bg-[#059669] text-white font-semibold rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                      >
                        {isSubmitting ? (
                          <>
                            <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            Đang Gửi...
                          </>
                        ) : (
                          'Gửi Yêu Cầu'
                        )}
                      </button>
                    </div>
                  </form>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* Footer */}
      <footer className="border-t border-gray-800/50 py-12 px-6">
        <div className="max-w-7xl mx-auto text-center">
          <p className="text-gray-500 text-sm mb-4">© 2026 Nhamy.us. Bảo lưu mọi quyền.</p>
          <div className="flex justify-center gap-6">
            <Link to="/terms" className="text-gray-400 hover:text-[#10b981] text-sm transition-colors">Điều Khoản Dịch Vụ</Link>
            <Link to="/privacy" className="text-gray-400 hover:text-[#10b981] text-sm transition-colors">Chính Sách Bảo Mật</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
